import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaCog,
  FaBell,
  FaQuestionCircle,
  FaSignOutAlt,
  FaUserCircle,
  FaPlus,
  FaHistory,
} from 'react-icons/fa';
import { FiChevronLeft } from 'react-icons/fi';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { createChatSocket } from '../utils/chatSocket';
import {
  clearAllChatNotifications,
  getCombinedNotifications,
  getCombinedUnreadTotal,
  pushAnnouncementNotification,
  pushChatNotification,
} from '../utils/chatNotifications';
import { graphqlRequest } from '../utils/graphqlRequest';
import {
  buildAdminNotificationUrl,
  getAdminNotificationDestination,
} from '../utils/notificationPreferences';
import '../styles/NavBar.css';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  readStoredUserRoles,
  isSuperAdminUser,
  shouldOpenAdminProfileHub,
  isBranchSuperAdminUser,
} from '../utils/adminProfileRouting';
import { adminPath, ADMIN_ROUTES } from '../constants/adminPortal';
import {
  getTenantBranding,
  TENANT_BRANDING_EVENT,
} from '../utils/tenantBranding';

const GET_BACKEND_NOTIFICATIONS = `
  query GetNotifications($userId: String) {
    getNotifications(userId: $userId) {
      id
      userId
      title
      message
      createdAt
    }
  }
`;

const backendSeenKey = (userId) => `admin-backend-notif-last-seen:${userId || 'unknown'}`;

function parseProfileNoteMeta(message) {
  const raw = String(message || '');
  const m = raw.match(/\[\[profileNote:([^:\]]+):([^:\]]+)\]\]/);
  if (!m) return { cleanMessage: raw, userId: '', noteId: '' };
  const cleanMessage = raw.replace(m[0], '').trim();
  return { cleanMessage, userId: m[1] || '', noteId: m[2] || '' };
}

const NavBar = ({ showBackButton = false, onBack, onAddLead }) => {
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const [unreadTotal, setUnreadTotal] = useState(() => getCombinedUnreadTotal());
  const [backendUnread, setBackendUnread] = useState(0);
  const [backendItems, setBackendItems] = useState([]);
  const [localNotifications, setLocalNotifications] = useState(() =>
    getCombinedNotifications(),
  );
  const [showNotificationMenu, setShowNotificationMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileRef = useRef(null);
  const bellRef = useRef(null);

  // White-label branding (logo / brand name). Updates live when branding loads
  // or the tenant saves new settings.
  const [branding, setBranding] = useState(() => getTenantBranding());
  useEffect(() => {
    const onBranding = (e) => setBranding(e.detail || getTenantBranding());
    window.addEventListener(TENANT_BRANDING_EVENT, onBranding);
    return () => window.removeEventListener(TENANT_BRANDING_EVENT, onBranding);
  }, []);
  const accessToken = Cookies.get('accessToken');
  const currentUserId = useMemo(() => {
    const fromCookie = Cookies.get('userId');
    if (fromCookie) return String(fromCookie);
    if (!accessToken) return '';
    try {
      const decoded = jwtDecode(accessToken);
      return decoded?.sub?.userId || '';
    } catch {
      return '';
    }
  }, [accessToken]);

  const dropdownNotifications = useMemo(() => {
    const localOnly = (Array.isArray(localNotifications) ? localNotifications : []).filter(
      (item) => item?.type !== 'backend',
    );
    const merged = [...backendItems, ...localOnly];
    const seen = new Set();
    const deduped = merged.filter((item) => {
      if (!item?.id || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
    const timeMs = (item) => {
      const t = item?.createdAt ? new Date(item.createdAt).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };
    const isInventoryReminder = (item) =>
      /synthetic-(lowstock|expiry)-/i.test(String(item?.id || ''));
    return deduped.sort((a, b) => {
      const synA = isInventoryReminder(a);
      const synB = isInventoryReminder(b);
      if (synA !== synB) return synA ? 1 : -1;
      return timeMs(b) - timeMs(a);
    });
  }, [backendItems, localNotifications]);

  useEffect(() => {
    const syncUnread = () => {
      setUnreadTotal(getCombinedUnreadTotal());
      setLocalNotifications(getCombinedNotifications());
    };
    syncUnread();
    window.addEventListener('admin-chat-unread-updated', syncUnread);
    return () => window.removeEventListener('admin-chat-unread-updated', syncUnread);
  }, []);

  // Backend notifications (profile notes) unread badge + dropdown list.
  useEffect(() => {
    if (!currentUserId) return undefined;
    let cancelled = false;

    const computeUnread = async () => {
      try {
        const data = await graphqlRequest(GET_BACKEND_NOTIFICATIONS, { userId: currentUserId });
        const list = Array.isArray(data?.getNotifications) ? data.getNotifications : [];
        const normalized = list.map((n) => {
          const { cleanMessage, userId: targetUserId, noteId } = parseProfileNoteMeta(n?.message);
          return {
            id: `backend-${n.id}`,
            type: 'backend',
            title: n?.title || 'Notification',
            content: cleanMessage || '',
            createdAt: n?.createdAt,
            meta: { targetUserId, noteId },
          };
        });
        const lastSeenRaw = localStorage.getItem(backendSeenKey(currentUserId));
        const lastSeen = lastSeenRaw ? new Date(lastSeenRaw).getTime() : 0;
        const count = list.filter((n) => {
          if (/synthetic-(lowstock|expiry)-/i.test(String(n?.id || ''))) {
            return false;
          }
          const t = new Date(n?.createdAt || 0).getTime();
          return Number.isFinite(t) && t > lastSeen;
        }).length;
        if (!cancelled) {
          setBackendItems(normalized);
          setBackendUnread(count);
        }
      } catch {
        if (!cancelled) {
          setBackendUnread(0);
          setBackendItems([]);
        }
      }
    };

    computeUnread();
    const id = window.setInterval(computeUnread, 30000);

    const onSeen = () => computeUnread();
    const onRefresh = () => computeUnread();
    window.addEventListener('admin-backend-notifications-seen', onSeen);
    window.addEventListener('admin-backend-notifications-refresh', onRefresh);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener('admin-backend-notifications-seen', onSeen);
      window.removeEventListener('admin-backend-notifications-refresh', onRefresh);
    };
  }, [currentUserId]);

  useEffect(() => {
    if (!accessToken) return undefined;

    const socket = createChatSocket(accessToken);
    socket.on('notification.message', (event = {}) => {
      const isMine = event?.senderId && event.senderId === currentUserId;
      if (isMine) return;

      pushChatNotification({
        eventId: event?.eventId,
        senderId: event?.senderId || '',
        senderName: event?.senderName || 'New message',
        content: event?.content || '',
        attachmentUrls: Array.isArray(event?.attachmentUrls) ? event.attachmentUrls : [],
        createdAt: event?.createdAt || new Date().toISOString(),
        conversationId: event?.conversationId || '',
        incrementUnread: true,
      });
    });

    socket.on('announcement.created', (event = {}) => {
      pushAnnouncementNotification({
        eventId: event?.eventId,
        title: event?.title || 'Announcement',
        message: event?.message || '',
        senderName: event?.senderName || 'Announcement',
        createdAt: event?.createdAt || new Date().toISOString(),
      });
      // getNotifications row is written server-side; refresh badge without waiting for 30s poll
      window.setTimeout(() => {
        window.dispatchEvent(new Event('admin-backend-notifications-refresh'));
      }, 400);
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [accessToken, currentUserId]);

  const markBackendNotificationsSeen = () => {
    if (!currentUserId) return;
    localStorage.setItem(backendSeenKey(currentUserId), new Date().toISOString());
    window.dispatchEvent(new Event('admin-backend-notifications-seen'));
  };

  const handleBellClick = () => {
    setShowProfileMenu(false);
    setShowNotificationMenu((prev) => {
      const next = !prev;
      if (next) markBackendNotificationsSeen();
      return next;
    });
  };

  const handleClearAllNotifications = () => {
    clearAllChatNotifications();
    markBackendNotificationsSeen();
    setLocalNotifications(getCombinedNotifications());
  };

  const openNotificationDestination = (item) => {
    setShowNotificationMenu(false);
    if (item?.type === 'backend') {
      const meta = item?.meta || {};
      if (meta?.targetUserId && meta?.noteId) {
        navigate(
          `/profile-dashboard/${meta.targetUserId}?tab=notes&noteId=${meta.noteId}`,
        );
        return;
      }
      const dest = getAdminNotificationDestination({
        type: 'announcement',
        title: item?.title,
        content: item?.content,
      });
      navigate(buildAdminNotificationUrl(dest));
      return;
    }
    const dest = getAdminNotificationDestination(item);
    navigate(buildAdminNotificationUrl(dest));
  };

  const pageMap = {
    all: 'lead',
    pipeline: 'pipeline',
    calender: 'calendar',
    services: 'services',
  };

  // Keep dropdown + search box in sync with the URL (so filters stick after navigate).
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const page = params.get('page') || '';
    const search = params.get('search') || '';
    const reverse = {
      lead: 'all',
      pipeline: 'pipeline',
      calendar: 'calender',
      services: 'services',
    };
    if (reverse[page]) setCategory(reverse[page]);
    setQuery(search);
  }, [location.search]);

  const handleCategoryChange = (e) => {
    const nextCategory = e.target.value;
    setCategory(nextCategory);
    const page = pageMap[nextCategory] || 'lead';
    const params = new URLSearchParams({ page });
    const trimmed = query.trim();
    if (trimmed) params.set('search', trimmed);
    navigate(`/?${params.toString()}`);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;

    const page = pageMap[category] || 'lead';
    const params = new URLSearchParams({ page, search: trimmed });
    navigate(`/?${params.toString()}`);
  };

  const displayName = Cookies.get('userName') || 'User';
  const roleLabel = (() => {
    try {
      const roles = JSON.parse(localStorage.getItem('userRoles') || '[]');
      return Array.isArray(roles) && roles.length > 0 ? String(roles[0]) : 'Admin';
    } catch {
      return 'Admin';
    }
  })();

  const profileMenuRoles = readStoredUserRoles();
  const useAdminProfileHub = shouldOpenAdminProfileHub(profileMenuRoles);
  const isSuperAdmin = isSuperAdminUser(profileMenuRoles);
  const isBranchSuperAdmin = isBranchSuperAdminUser(profileMenuRoles);

  const handleLogout = () => {
    Cookies.remove('accessToken');
    Cookies.remove('companyId');
    Cookies.remove('companyName');
    Cookies.remove('userName');
    localStorage.clear();
    setShowProfileMenu(false);
    window.location.href = '/login';
  };

  useEffect(() => {
    const onDocDown = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
      if (bellRef.current && !bellRef.current.contains(e.target)) {
        setShowNotificationMenu(false);
      }
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  return (
    <nav className="navbar">
      <div className="navbar-brand">
        {showBackButton && (
          <button type="button" className="back-button" onClick={onBack} aria-label="Back">
            <FiChevronLeft size={28} />
          </button>
        )}
        <div className="logo">
          <img
            src={branding?.logoUrl || '/logoSas.png?v=5'}
            alt={branding?.brandName || branding?.tenantName || 'SAS Cleaning Suite'}
            onError={(e) => {
              // Absolute path — relative `./logoSas.png` breaks on nested routes
              // (e.g. /support-management/…) and causes a load/error flicker loop.
              if (e.currentTarget.dataset.fallbackApplied === '1') return;
              e.currentTarget.dataset.fallbackApplied = '1';
              e.currentTarget.src = '/logoSas.png?v=5';
            }}
          />
        </div>
      </div>

      <form className="search-container" onSubmit={handleSearch}>
        <select
          className="dropdown-nav"
          value={category}
          onChange={handleCategoryChange}
          aria-label="Search category"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="all">All</option>
          <option value="pipeline">Pipeline</option>
          <option value="calender">Calender</option>
          <option value="services">Services</option>
        </select>
        <input
          type="text"
          placeholder="Search here..."
          className="search-box"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </form>

      <div className="navbar-actions">
        <button className="navbar-add-lead-btn" onClick={onAddLead} type="button">
          <FaPlus className="plus-icon" />
          <span className="add-lead-label">Lead</span>
        </button>

        <button
          type="button"
          className="nav-bar-icon-btn"
          onClick={() => navigate('/account-settings')}
          title="Settings"
          aria-label="Settings"
        >
          <FaCog className="nav-bar-icon" />
        </button>
        <div className="admin-notification-container" ref={bellRef}>
          <button
            type="button"
            className="nav-bar-icon-btn notification-bell-button"
            onClick={handleBellClick}
            title="Notifications"
            aria-label="Notifications"
            aria-expanded={showNotificationMenu}
          >
            <FaBell className="nav-bar-icon" />
            {(() => {
              const bellCount = unreadTotal + backendUnread;
              return bellCount > 0 ? (
                <span className="notification-bell-badge">
                  {bellCount > 99 ? '99+' : bellCount}
                </span>
              ) : null;
            })()}
          </button>
          {showNotificationMenu ? (
            <div className="admin-notification-popup-menu">
              <div className="admin-notification-popup-header">
                <p>Notifications</p>
                {dropdownNotifications.length > 0 ? (
                  <button
                    type="button"
                    className="admin-notification-clear"
                    onClick={handleClearAllNotifications}
                  >
                    Clear all
                  </button>
                ) : null}
              </div>
              <div className="admin-notification-popup-body">
                {dropdownNotifications.length === 0 ? (
                  <p className="admin-notification-empty">No notifications yet.</p>
                ) : (
                  dropdownNotifications.slice(0, 8).map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className="admin-notification-item"
                      onClick={() => openNotificationDestination(item)}
                    >
                      <p className="admin-notification-item-title">
                        {item.type === 'announcement' || item.type === 'backend'
                          ? item.title || 'Notification'
                          : item.senderName || 'New message'}
                      </p>
                      <p className="admin-notification-item-message">
                        {item.type === 'announcement' || item.type === 'backend'
                          ? item.content || ''
                          : item.content ||
                            (item.hasAttachment ? 'Sent an attachment' : 'New message')}
                      </p>
                    </button>
                  ))
                )}
              </div>
              <div className="admin-notification-popup-footer">
                <button
                  type="button"
                  className="admin-notification-settings-link"
                  onClick={() => {
                    setShowNotificationMenu(false);
                    navigate(adminPath(ADMIN_ROUTES.NOTIFICATIONS));
                  }}
                >
                  Notification settings
                </button>
              </div>
            </div>
          ) : null}
        </div>
        <div className="admin-profile-container" ref={profileRef}>
          <button
            type="button"
            className="admin-profile-btn"
            onClick={() => {
              setShowNotificationMenu(false);
              setShowProfileMenu((v) => !v);
            }}
            title="Profile"
          >
            <FaUserCircle className="nav-bar-icon nav-bar-icon--profile" />
            <span className="admin-profile-text">Profile</span>
          </button>

          {showProfileMenu && (
            <div className="admin-profile-popup-menu">
              <div className="admin-profile-popup-header">
                <div className="admin-profile-popup-avatar">
                  <FaUserCircle />
                </div>
                <div>
                  <p className="admin-profile-popup-name">{displayName}</p>
                  <p className="admin-profile-popup-role">{roleLabel}</p>
                </div>
              </div>

              <button
                type="button"
                className="admin-profile-popup-item admin-profile-popup-item-feature"
                onClick={() => {
                  setShowProfileMenu(false);
                  if (isSuperAdmin || isBranchSuperAdmin || useAdminProfileHub) {
                    navigate(adminPath(ADMIN_ROUTES.PROFILE_DASHBOARD));
                  } else {
                    navigate(adminPath('profile'));
                  }
                }}
              >
                <span className="admin-profile-popup-item-icon-wrap">
                  <FaUserCircle className="admin-profile-popup-item-icon" />
                </span>
                <span>
                  {isSuperAdmin || isBranchSuperAdmin || useAdminProfileHub
                    ? 'My profile'
                    : 'Personal Information'}
                </span>
              </button>

              {isSuperAdmin ? (
                <>
                  <button
                    type="button"
                    className="admin-profile-popup-item admin-profile-popup-item-feature"
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate('/branch-management');
                    }}
                  >
                    <span className="admin-profile-popup-item-icon-wrap">
                      <FaCog className="admin-profile-popup-item-icon" />
                    </span>
                    <span>Branch management</span>
                  </button>
                </>
              ) : null}

              {isSuperAdmin || isBranchSuperAdmin ? (
                <>
                  <button
                    type="button"
                    className="admin-profile-popup-item admin-profile-popup-item-feature"
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate('/role-profiles');
                    }}
                  >
                    <span className="admin-profile-popup-item-icon-wrap">
                      <FaHistory className="admin-profile-popup-item-icon" />
                    </span>
                    <span>Role profiles</span>
                  </button>

                  <button
                    type="button"
                    className="admin-profile-popup-item admin-profile-popup-item-feature"
                    onClick={() => {
                      setShowProfileMenu(false);
                      navigate(
                        adminPath(`${ADMIN_ROUTES.USER_ACTIVITY}?tab=sessions`),
                      );
                    }}
                  >
                    <span className="admin-profile-popup-item-icon-wrap">
                      <FaHistory className="admin-profile-popup-item-icon" />
                    </span>
                    <span>User activity</span>
                  </button>
                </>
              ) : null}

              <button
                type="button"
                className="admin-profile-popup-item admin-profile-popup-item-feature"
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate(adminPath('account-settings'));
                }}
              >
                <span className="admin-profile-popup-item-icon-wrap">
                  <FaCog className="admin-profile-popup-item-icon" />
                </span>
                <span>Account Settings</span>
              </button>

              <button
                type="button"
                className="admin-profile-popup-item admin-profile-popup-item-feature"
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate(adminPath(ADMIN_ROUTES.NOTIFICATIONS));
                }}
              >
                <span className="admin-profile-popup-item-icon-wrap">
                  <FaBell className="admin-profile-popup-item-icon" />
                </span>
                <span>Notification settings</span>
              </button>

              <button
                type="button"
                className="admin-profile-popup-item admin-profile-popup-item-feature"
                onClick={() => {
                  setShowProfileMenu(false);
                  navigate(adminPath(ADMIN_ROUTES.HELP_SUPPORT));
                }}
              >
                <span className="admin-profile-popup-item-icon-wrap">
                  <FaQuestionCircle className="admin-profile-popup-item-icon" />
                </span>
                <span>Help &amp; Support</span>
              </button>

              <button
                type="button"
                className="admin-profile-popup-item admin-profile-popup-item-logout"
                onClick={handleLogout}
              >
                <FaSignOutAlt />
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;
